/**
 * PersonalProfileForm
 * Form for editing personal user profile information
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { InputWithIcon, SelectWithIcon } from '@shared/components/form'
import { CitySelector } from '@features/search/components'
import { PhoneInput } from '@shared/components/forms'
import { Button } from '@shared/components/ui/button'
import {
  generateValidationMessage,
  openWhatsAppChat,
  useBotContact,
  useUpdateAccountInfo,
  useValidationCode,
  useValidationStatus,
} from '@shared/lib/api/hooks'
import type { User as UserType } from '@shared/types'
import { useLoading } from '@shared/contexts'
import { GraduationCap, Loader2, Mail, MapPin, User } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { useUpdateProfile } from '../hooks/useUserProfile'
import { personalProfileSchema, type PersonalProfileFormData } from '../schemas/profileSchemas'
import { ValidationStatusBadge } from './ValidationStatusBadge'
import { logger } from '@shared/lib/utils/logger'

// ============================================================================
// Types
// ============================================================================

interface PersonalProfileFormProps {
  user: UserType
  onSuccess?: () => void
  onCancel?: () => void
}

// ============================================================================
// Component
// ============================================================================

export function PersonalProfileForm({ user, onSuccess, onCancel }: PersonalProfileFormProps) {
  const updateProfile = useUpdateProfile()
  const { showLoading, hideLoading } = useLoading()

  const [professionalType, setProfessionalType] = useState<string>(
    user?.professionalType || ''
  )

  // Validation state
  const [isValidating, setIsValidating] = useState(false)
  const { data: botContact } = useBotContact()
  const { refetch: refetchValidationCode } = useValidationCode()
  const { refetch: refetchValidation } = useValidationStatus()

  const updateAccountInfo = useUpdateAccountInfo()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    trigger,
    getValues,
  } = useForm<PersonalProfileFormData>({
    resolver: zodResolver(personalProfileSchema),
    defaultValues: {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      cityId: user.cityId || user.city || '',
      gender: user.gender || undefined,
      professionalType: '',
      licenseNumber: user.licenseNumber || '',
    },
  })

  const cityId = watch('cityId')
  const phone = watch('phone')
  const gender = watch('gender')

  // Handle account validation
  const handleValidateAccount = async () => {
    // 1. Trigger Zod validation for all fields
    const isValid = await trigger()

    if (!isValid) {
      // Zod validation failed - form will automatically highlight invalid fields
      toast.error('Por favor completa todos los campos requeridos correctamente')

      // Scroll to first error field
      const errorFields = Object.keys(errors)
      const firstErrorField = errorFields[0]
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`)
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      return
    }

    setIsValidating(true)

    try {
      // 2. Get validated form values (guaranteed valid by Zod)
      const values = getValues()

      // 3. Update account information
      showLoading('Actualizando información de cuenta...')
      await updateAccountInfo.mutateAsync({
        name: values.firstName,
        lastName: values.lastName,
        email: values.email || '',
        phoneNumber: values.phone,
        cityId: values.cityId || '',
        gender: values.gender,
      })

      // 4. Get bot contact
      if (!botContact) {
        hideLoading()
        toast.error('No se pudo obtener el contacto del bot')
        setIsValidating(false)
        return
      }

      // 5. Fetch validation code on-demand
      showLoading('Obteniendo código de validación...')
      const { data: validationCode } = await refetchValidationCode()

      if (!validationCode) {
        hideLoading()
        toast.error('No se pudo obtener el código de validación')
        setIsValidating(false)
        return
      }

      const message = generateValidationMessage(validationCode)

      // 6. Show loading message and open WhatsApp
      showLoading('Abriendo WhatsApp para validación...')
      openWhatsAppChat(botContact, message)
      showLoading('Esperando validación de cuenta...')

      // 7. Start polling for validation status
      let count = 0
      const maxAttempts = 5
      const pollInterval = 5000 // 5 seconds

      const interval = setInterval(() => { void (async () => {
        if (count < maxAttempts) {
          const { data: isValidated } = await refetchValidation()

          if (isValidated) {
            // SUCCESS - Account validated!
            clearInterval(interval)
            setIsValidating(false)
            hideLoading()
            toast.success('¡Cuenta validada exitosamente!')

            // Call success callback (will execute pending actions)
            onSuccess?.()
          }
          count++
        } else {
          // TIMEOUT - 25 seconds passed (5 attempts × 5 seconds)
          clearInterval(interval)
          setIsValidating(false)
          hideLoading()
          toast.error('No se ha podido validar tu cuenta. Por favor, intenta nuevamente.')
        }
      })() }, pollInterval)
    } catch (error) {
      setIsValidating(false)
      hideLoading()
      toast.error(
        error instanceof Error
          ? error.message
          : 'Error al actualizar la información'
      )
    }
  }

  // Handle form submission
  const onSubmit = async (data: PersonalProfileFormData) => {
    try {
      showLoading('Guardando perfil personal...')
      await updateProfile.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || '',
        phone: data.phone,
        cityId: data.cityId,
        gender: data.gender,
      })

      toast.success('Perfil actualizado correctamente')
      // Don't hide loading here - let onSuccess handle it to keep overlay visible during redirect
      onSuccess?.()
    } catch (error) {
      logger.error('Failed to update profile:', error)
      toast.error('Error al actualizar el perfil')
      hideLoading()
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-6">
      {/* Validation Status */}
      <div className="flex flex-col items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium">Estado de validación</span>
          <ValidationStatusBadge isValidated={user?.isAccountValidated || false} />
        </div>

        {/* Validation Button - Only show if NOT validated */}
        {!user?.isAccountValidated && (
          <div className="w-full pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleValidateAccount()}
              disabled={isValidating || isSubmitting}
              className="flex-1 w-full"
            >
              {isValidating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isValidating ? 'Validando...' : 'Validá tu cuenta'}
            </Button>
          </div>
        )}

      </div>

      {/* Personal Information Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Información Personal
          <User className="h-6 w-6 float-left mr-2" />
          {/* <ChevronDown className="h-6 w-6 float-right text-black" /> */}
        </h3>

        {/* First Name */}
        <div>
          <InputWithIcon
            type="text"
            placeholder="Nombre"
            {...register('firstName')}
            disabled={isSubmitting}
          />
          {errors.firstName && (
            <p className="text-sm text-destructive mt-1">{errors.firstName.message}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <InputWithIcon
            type="text"
            placeholder="Apellido"
            {...register('lastName')}
            disabled={isSubmitting}
          />
          {errors.lastName && (
            <p className="text-sm text-destructive mt-1">{errors.lastName.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <InputWithIcon
            icon={Mail}
            type="email"
            placeholder="Correo electrónico"
            {...register('email')}
            disabled={isSubmitting || !!user.email}
          />
          {errors.email && (
            <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
          )}
          {user.email && (
            <p className="text-sm text-muted-foreground mt-1">
              El correo electrónico no puede ser modificado
            </p>
          )}
        </div>

        {/* Phone */}
        <div>
          <PhoneInput
            value={phone}
            onChange={(value) => setValue('phone', value)}
            disabled={isSubmitting || (!!user.phone && user.isPhoneValidated)}
          />
          {errors.phone && (
            <p className="text-sm text-destructive mt-1">{errors.phone.message}</p>
          )}
          {user.phone && user.isPhoneValidated && (
            <p className="text-sm text-muted-foreground mt-1">
              El teléfono validado no puede ser modificado
            </p>
          )}
        </div>

        {/* City */}
        <div>
          <CitySelector
            placeholder="Ciudad"
            value={cityId}
            onChange={(value) => setValue('cityId', value)}
            disabled={isSubmitting}
            rightIcon={MapPin}
          />
          {errors.cityId && (
            <p className="text-sm text-destructive mt-1">{errors.cityId.message}</p>
          )}
        </div>

        {/* Gender */}
        <div>
          <SelectWithIcon
            placeholder="Género"
            value={gender || ''}
            onValueChange={(value) => setValue('gender', value as 'male' | 'female', { shouldValidate: true })}
            options={[
              { value: 'male', label: 'Masculino' },
              { value: 'female', label: 'Femenino' },
            ]}
            disabled={isSubmitting}
          />
          {errors.gender && (
            <p className="text-sm text-destructive mt-1">{errors.gender.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Opcional</h3>
        {/* Professional Type & License Number */}
        <div className="grid grid-cols-2 gap-4">
          {/* Professional Type Dropdown */}
          <div>
            <SelectWithIcon
              placeholder="Tipo profesional"
              value={professionalType}
              onValueChange={(value) => {
                const actualValue = value === 'none' ? '' : value
                setProfessionalType(actualValue)
                setValue('professionalType', actualValue as '' | 'Arq' | 'Ing')
              }}
              options={[
                { value: 'none', label: 'Ninguno' },
                { value: 'Arq', label: 'Arquitecto' },
                { value: 'Ing', label: 'Ingeniero' }
              ]}
              disabled={isSubmitting}
            />
          </div>

          {/* License Number Input */}
          <div>
            <InputWithIcon
              icon={GraduationCap}
              type="text"
              placeholder="Matrícula"
              {...register('licenseNumber')}
              disabled={!professionalType || isSubmitting}
            />
          </div>
        </div>
      </div>


      {/* Form Actions */}
      <div className="flex gap-4 pt-2">
        <Button
          type="submit"
          disabled={isSubmitting || isValidating}
          className="flex-1"
        >
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
        </Button>

        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting || isValidating}
          >
            Cancelar
          </Button>
        )}
      </div>

      {/* Success/Error Messages */}
      {updateProfile.isSuccess && (
        <p className="text-sm text-green-600">
          ¡Perfil actualizado correctamente!
        </p>
      )}
      {updateProfile.isError && (
        <p className="text-sm text-destructive">
          Error al actualizar el perfil. Por favor, intenta de nuevo.
        </p>
      )}
    </form>
  )
}
